import React, { ComponentProps, useCallback, useEffect, useMemo } from 'react';
import debounce from 'lodash-es/debounce';

import { Form, useKibbleForm } from '@rover/kibble/forms';

import { LocationOnChangeData } from '../../../search/SearchPage/components/Filters/Location';
import { parseCoordinate } from '../utilities';

import { FormState, schema } from './filterFormState';
import TrainingLocationInput from './TrainingLocationInput';

export interface FilterFormProps {
  onSubmit: (formData: FormState) => void;
  query: Partial<FormState>;
}
export default function FilterForm({ onSubmit, query }: FilterFormProps): JSX.Element {
  const {
    formProps,
    setValue: setFormValue,
    handleSubmit,
    watch,
  } = useKibbleForm({
    schema,
    defaultValues: query,
  });

  // watch is necessary here, as opposed to useController, because useController's field.value does
  // not update when setFormValue is set to undefined. Be mindful of this in any future refactors.
  const locationFieldValue = watch('location');

  const updateFormLocationData = useCallback(
    (locationData: LocationOnChangeData | typeof query): void => {
      const { location, centerlat, centerlng, locationType, locationAccuracy, rawLocationTypes } =
        locationData;

      setFormValue('location', location);
      setFormValue('centerlat', parseCoordinate(centerlat));
      setFormValue('centerlng', parseCoordinate(centerlng));
      setFormValue('locationType', locationType);
      setFormValue('locationAccuracy', locationAccuracy);
      setFormValue('rawLocationTypes', rawLocationTypes);
    },
    [setFormValue]
  );

  // If the location in the query was changed by the server, update the form to reflect that,
  // unless the locationFieldValue is blank, then we preserve that.
  useEffect(() => {
    if (query.location && locationFieldValue && query.location !== locationFieldValue) {
      updateFormLocationData(query);
    }
  }, [locationFieldValue, query, updateFormLocationData]);

  const submitForm = useMemo(() => handleSubmit(onSubmit), [handleSubmit, onSubmit]);

  const handleLocationChange: ComponentProps<typeof TrainingLocationInput>['onChange'] =
    useMemo(() => {
      // Debounce as LocationInput triggers onChange twice
      return debounce((locationData: LocationOnChangeData): void => {
        updateFormLocationData(locationData);
        submitForm();
      });
    }, [submitForm, updateFormLocationData]);

  return (
    <Form {...formProps} hideRequiredFieldWarning>
      <TrainingLocationInput
        locationInputId="location-input-sidebar"
        onChange={handleLocationChange}
        location={locationFieldValue}
      />
    </Form>
  );
}
